import React from "react"
import { useTranslation } from "react-i18next"
import { FileUploader } from "react-drag-drop-files"

import { Box, Image, Typography } from "src/UILibrary"

import FileSVG from "../../assets/icons/file.svg"
import { ProfileService } from "src/openapi"
import { RESPONSE_CODE, SNACKBAR_TYPES } from "src/modules/const"
import { useRecoilState } from "recoil"
import { ShowToast } from "src/states/showToast"

type Props = {
  name?: string
  required?: boolean
  file?: string
  handleChange: any
  validationMessage?: string
  labelStyle?: any
  noUpload?: boolean
  disabled?: boolean
}

const FileSelectField = ({
  name,
  required,
  file,
  handleChange,
  validationMessage,
  labelStyle,
  noUpload,
  disabled,
}: Props) => {
  const { t } = useTranslation("translation", { keyPrefix: "base" })

  const [, setShowToast] = useRecoilState(ShowToast)

  const handleSelected = (file: File) => {
    if (noUpload) {
      let reader = new FileReader()

      reader.readAsDataURL(file)
      reader.onloadend = function () {
        handleChange(reader.result)
      }.bind(this)
    } else {
      ProfileService.uploadFile({
        file: file,
      })
        .then((res) => {
          if (res.status === RESPONSE_CODE.SUCCESS) {
            handleChange(res.data)
          } else {
            setShowToast({ show: true, text: t("file_upload_failed"), type: SNACKBAR_TYPES.ERROR })
          }
        })
        .catch(() => {
          setShowToast({ show: true, text: t("file_upload_failed"), type: SNACKBAR_TYPES.ERROR })
        })
    }
  }

  return (
    <Box display="block">
      {name && (
        <Box display="flex" sx={{ mb: "10px" }}>
          <Typography.SubHeadNormal sx={{ mr: "10px", ...labelStyle }}>
            {name}
          </Typography.SubHeadNormal>
          {required && (
            <Box
              sx={{
                px: "8px",
                borderWidth: "1px",
                borderStyle: "solid",
                borderRadius: "4px",
                borderColor: "text.border",
                display: "flex",
              }}
            >
              <Typography.InputFieldRequired sx={{ m: "auto", color: "text.border" }}>
                {t("required")}
              </Typography.InputFieldRequired>
            </Box>
          )}
        </Box>
      )}

      <FileUploader
        classes="file-select"
        types={["png", "jpeg", "jpg", "gif"]}
        hoverTitle=" "
        handleChange={(file: File) => handleSelected(file)}
        disabled={disabled}
      >
        <FileContent file={file} noUpload={noUpload} />
      </FileUploader>
      {validationMessage && (
        <Box sx={{ height: "20px", pt: "5px" }}>
          <Typography.ValidationText>{validationMessage}</Typography.ValidationText>
        </Box>
      )}
    </Box>
  )
}

type ContentType = {
  file?: string
  noUpload?: boolean
}

const FileContent = ({ file, noUpload }: ContentType) => {
  const { t } = useTranslation("translation", { keyPrefix: "base" })

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        borderWidth: "1px",
        borderColor: "#C69989",
        borderStyle: "dashed",
        backgroundColor: "#F1F1F1",
        ...(noUpload
          ? { width: "320px", height: "160px", justifyContent: "center" }
          : { maxWidth: "350px" }),
      }}
    >
      {file ? (
        <Image src={file} />
      ) : (
        <Box textAlign="center" sx={{ px: "80px", py: "36px" }}>
          <Image src={FileSVG} sx={{ mx: "auto", mb: "6px" }} />
          <Typography.FileFieldDetail sx={{ mx: "auto" }}>
            {t("drop_file")}
          </Typography.FileFieldDetail>
          <Typography.FileFieldDetail sx={{ mt: "6px", mb: "6px", mx: "auto" }}>
            {t("or")}
          </Typography.FileFieldDetail>
          <Box
            sx={{
              display: "flex",
              width: "86px",
              px: "5px",
              py: "2px",
              mx: "auto",
              borderWidth: "1px",
              borderColor: "#C69989",
              borderStyle: "solid",
              backgroundColor: "white",
            }}
          >
            <Typography.FileFieldDetail sx={{ my: "auto", fontSize: "10px", color: "#C69989" }}>
              {t("select_file")}
            </Typography.FileFieldDetail>
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default FileSelectField
